import {
  APP_ENVS,
  currentAppEnvironment,
} from '@savgroup-front-common/constants/shared';

import {
  LATENCY_SOURCE,
  LatencySource,
} from '../../services/networkLatencyObserver';

export const DEGRADED_EFFECTIVE_TYPES = ['slow-2g', '2g'] as const;

export const MAX_ACCEPTABLE_RTT_MS = 500;

export const MIN_ACCEPTABLE_DOWNLINK_MBPS = 1;

const DEFAULT_MAX_ACCEPTABLE_AVG_LATENCY_MS = 3000;

const RELAXED_MAX_ACCEPTABLE_AVG_LATENCY_MS = 60_000;

const MAX_ACCEPTABLE_AVG_LATENCY_MS_BY_ENV: Partial<Record<APP_ENVS, number>> = {
  [APP_ENVS.DEMO]: RELAXED_MAX_ACCEPTABLE_AVG_LATENCY_MS,
  [APP_ENVS.PROD]: RELAXED_MAX_ACCEPTABLE_AVG_LATENCY_MS,
};

export const MAX_ACCEPTABLE_AVG_LATENCY_MS =
  MAX_ACCEPTABLE_AVG_LATENCY_MS_BY_ENV[currentAppEnvironment] ??
  DEFAULT_MAX_ACCEPTABLE_AVG_LATENCY_MS;

/**
 * Default threshold for a backend-independent network latency average, used as
 * a fallback when the measurement source is unknown. Intentionally tighter than
 * {@link MAX_ACCEPTABLE_AVG_LATENCY_MS} and not relaxed per environment — slow
 * backends no longer inflate the measurement.
 */
export const MAX_ACCEPTABLE_NETWORK_LATENCY_MS = 1000;

/**
 * A single round-trip measurement (`probe`, `server-timing`): ~1 RTT, so a few
 * hundred ms already means a degraded 2g/slow-3g link.
 */
const MAX_ACCEPTABLE_SINGLE_RTT_LATENCY_MS = 400;

/**
 * A connection handshake (`connection`) spans DNS + TCP + TLS, i.e. several
 * round-trips, so its acceptable bound is correspondingly higher.
 */
const MAX_ACCEPTABLE_CONNECTION_LATENCY_MS = 1000;

/**
 * Per-source acceptable network latency. The threshold must match the number of
 * round-trips the source captures, otherwise a single-RTT probe of 456 ms would
 * never cross a handshake-sized bound.
 */
export const MAX_ACCEPTABLE_NETWORK_LATENCY_MS_BY_SOURCE: Partial<
  Record<LatencySource, number>
> = {
  [LATENCY_SOURCE.PROBE]: MAX_ACCEPTABLE_SINGLE_RTT_LATENCY_MS,
  [LATENCY_SOURCE.SERVER_TIMING]: MAX_ACCEPTABLE_SINGLE_RTT_LATENCY_MS,
  [LATENCY_SOURCE.CONNECTION]: MAX_ACCEPTABLE_CONNECTION_LATENCY_MS,
};

export const DEGRADED_TIMEOUT_COUNT_THRESHOLD = 2;

export const LATENCY_WINDOW_SIZE = 5;

export const ENTER_DEGRADED_DELAY_MS = 4000;

export const EXIT_DEGRADED_DELAY_MS = 8000;

export const NETWORK_TOAST_ID = 'network-status-toast';
